import { useParams, Link } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { ARTICLES } from "@/data/insights";
import { useSeo } from "@/hooks/use-seo";
import NotFound from "@/pages/not-found";
import { motion } from "framer-motion";
import { AdSlot } from "@/components/ads/AdSlot";
import { Comments } from "@/components/blog/Comments";

export default function InsightDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = ARTICLES.find((a) => a.slug === slug);

  useSeo({
    title: article ? `${article.title} | Mychromamind` : "Article not found | Mychromamind",
    description: article ? article.description : "This article could not be found.",
    path: article ? `/insights/${article.slug}` : "/insights",
    ogType: "article",
    noindex: !article,
    structuredData: article
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BlogPosting",
              headline: article.title,
              description: article.description,
              datePublished: article.date,
              dateModified: article.date,
              image: "https://mychromamind.com/og-image.png",
              articleSection: article.category,
              author: {
                "@type": "Person",
                name: "Daryoush Torabi Jafroudi",
                url: "https://mychromamind.com/about",
              },
              publisher: {
                "@type": "Organization",
                name: "Mychromamind",
                url: "https://mychromamind.com",
              },
              mainEntityOfPage: `https://mychromamind.com/insights/${article.slug}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://mychromamind.com/" },
                { "@type": "ListItem", position: 2, name: "Insights", item: "https://mychromamind.com/insights" },
                { "@type": "ListItem", position: 3, name: article.title, item: `https://mychromamind.com/insights/${article.slug}` },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!article) return <NotFound />;

  const related = ARTICLES.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3);

  return (
    <Shell>
      <article className="max-w-3xl mx-auto px-6 py-12 md:py-20 w-full">
        {/* Back */}
        <Link
          href="/insights"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-10"
        >
          ← Back to Insights
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium text-primary/70 uppercase tracking-wider">
              {article.category}
            </span>
            <span className="text-muted-foreground text-xs">·</span>
            <span className="text-xs text-muted-foreground">{article.readTime} min read</span>
            <span className="text-muted-foreground text-xs">·</span>
            <span className="text-xs text-muted-foreground">{article.date}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif leading-tight mb-6">{article.title}</h1>
          <p className="text-lg text-muted-foreground leading-relaxed border-l-2 border-primary/30 pl-4">
            {article.description}
          </p>
        </motion.header>

        {/* Body */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="prose prose-neutral max-w-none dark:prose-invert
            prose-p:leading-relaxed prose-p:text-foreground/90
            prose-h2:font-serif prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-li:text-foreground/90"
        >
          {article.sections.map((section, si) => (
            <section key={si}>
              {section.heading && <h2>{section.heading}</h2>}
              {section.body.map((para, pi) => (
                <p key={pi}>{para}</p>
              ))}
            </section>
          ))}
        </motion.div>

        {article.sources && article.sources.length > 0 && (
          <section
            aria-labelledby="research-sources-heading"
            className="mt-10 border-t border-border pt-8"
          >
            <h2
              id="research-sources-heading"
              className="font-serif text-xl mb-4"
            >
              Research sources
            </h2>
            <ul className="space-y-2 text-sm">
              {article.sources.map((source) => (
                <li key={source.url}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-4 hover:text-primary/80"
                  >
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <AdSlot
          slotId="2431849490"
          content={[article.description, ...article.sections.flatMap((s) => s.body)]}
          className="mt-10 pt-8 border-t border-border"
        />

        {/* Related articles — internal links to keep readers on-site and help
            search engines understand topical clusters within the blog. */}
        {related.length > 0 && (
          <div className="mt-16 pt-8 border-t border-border">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-5">
              More in {article.category}
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} href={`/insights/${r.slug}`}>
                  <div className="group h-full p-4 rounded-xl border border-border hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer">
                    <h3 className="text-sm font-serif leading-snug group-hover:text-primary transition-colors">
                      {r.title}
                    </h3>
                    <span className="text-xs text-muted-foreground mt-2 block">{r.readTime} min read</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Comments articleSlug={article.slug} />

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-border flex items-center justify-between">
          <Link
            href="/insights"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← All Insights
          </Link>
          <Link
            href="/learn"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            Explore Color Library →
          </Link>
        </div>
      </article>
    </Shell>
  );
}
